import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import PerText from '../../components/custom_components/Text'
import { ObjectivePaymentManualDeductionReason, ObjectivePayment, ObjectiveDto, Currency } from '../../types'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { Table, Tooltip } from 'antd'
import { useAuth } from '../../context/auth-context'
import { useAmountAndCurrency } from '../../shared/useAmountAndCurrency'
import { useDateTime } from '../../shared/useDateTime'
import PerSpacer from '../../components/custom_components/Spacer'
import { useCurrency } from '../../context/currency-context'
import { retrieveCurrencyConversionRate } from '../../shared/currencyConversionUtil'
import { round2digits } from '../appraisal/appraisal-flow/meeting/util/paymentsUtil'

interface PlannedPaymentTableItem {
  id: string
  title: ReactNode | string
  amount: ReactNode
  reached?: unknown
}

interface Props {
  objectivesToBePaid: ObjectiveDto[]
  objectivePayment?: ObjectivePayment
}
export const ConclusionObjectivePayment: FC<Props> = ({ objectivesToBePaid, objectivePayment }) => {
  const { user } = useAuth()
  const [t] = useTranslation()
  const { currency, currencyConversionData } = useCurrency()
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const { formatDateMonthYear } = useDateTime(user)

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      objectivesToBePaid
        .filter((obj): boolean => {
          if (obj.currency! !== currency) {
            const conversionRate: number | undefined = retrieveCurrencyConversionRate(obj.currency!, currency, currencyConversionData)
            if (!conversionRate) {
              return true
            }
          }
          return false
        })
        .map((obj) => obj.currency!),
    ),
  ]

  const getDeductionTitle = (reason: ObjectivePaymentManualDeductionReason) => {
    return (
      <>
        <PerText appearance="label-small">{t('appraisalMeeting.stepPayment.deduction.title')}</PerText>
        <PerText>{t(`appraisalMeeting.stepPayment.deduction.reason.${reason}`)}</PerText>
      </>
    )
  }

  const getDeductionAmount = (amount: number) => {
    return <PerText>- {formatCurrency(amount, currency)}</PerText>
  }

  const getObjectiveAmount = (amount: number, objectiveCurrency: Currency): JSX.Element => {
    if (!!amount && objectiveCurrency !== currency) {
      const hasUnknownCurrency: boolean = unknownCurrenciesForConversion.includes(objectiveCurrency)
      const conversionRate: number | undefined = retrieveCurrencyConversionRate(objectiveCurrency, currency, currencyConversionData)
      return (
        <PerFlexRow direction="column">
          <Tooltip
            title={
              hasUnknownCurrency
                ? t('currencyConversion.tooltips.tableConversionWarning', {
                    startingCurrency: objectiveCurrency,
                    targetCurrency: currency,
                  })
                : t('currencyConversion.tooltips.tableWarning')
            }
            placement="right"
          >
            <PerFlexRow>
              <PerText>{formatCurrency(amount, objectiveCurrency)}</PerText>
            </PerFlexRow>
            <PerFlexRow>
              <PerText
                fontWeight={hasUnknownCurrency ? 'bold' : undefined}
                color={hasUnknownCurrency ? 'error' : undefined}
                fontSize="xs"
                mt="xxs"
              >
                {calculateAndFormatNewTargetAmount(amount, currency, conversionRate)}
              </PerText>
            </PerFlexRow>
          </Tooltip>
        </PerFlexRow>
      )
    } else {
      return <PerText>{formatCurrency(amount, currency)}</PerText>
    }
  }

  const footer = () => {
    const targetBonusToBePaid = objectivesToBePaid.reduce((sum, obj) => {
      if (obj.achievedAmount) {
        if (obj.currency !== currency) {
          const conversionRate: number | undefined = retrieveCurrencyConversionRate(obj.currency!, currency, currencyConversionData)
          if (conversionRate === undefined) {
            return sum
          } else {
            return sum + Number((obj.achievedAmount * conversionRate).toFixed(2))
          }
        } else {
          return sum + obj.achievedAmount
        }
      } else {
        return sum
      }
    }, 0)

    const deductionAmount = objectivePayment && objectivePayment.deduction ? objectivePayment.deduction : 0
    const toPay = round2digits(targetBonusToBePaid - deductionAmount)

    return (
      <PerFlexRow justify="space-between">
        <PerText fontSize="s">{t('appraisalMeeting.stepPayment.table.footer')}</PerText>
        <PerText
          color={toPay < 0 || !!unknownCurrenciesForConversion.length ? 'error' : 'primary'}
          tooltip={
            !!unknownCurrenciesForConversion.length
              ? t('currencyConversion.tooltips.unknownConversionRate', {
                  currency,
                  unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                })
              : undefined
          }
          fontWeight={!!unknownCurrenciesForConversion.length ? 'bold' : undefined}
        >
          {!!unknownCurrenciesForConversion.length && calculateAndFormatNewTargetAmount(toPay, currency)}
          {!unknownCurrenciesForConversion.length && formatCurrency(toPay, currency)}
        </PerText>
      </PerFlexRow>
    )
  }

  const columns = [
    {
      key: 'title',
      title: t('appraisalMeeting.conclusion.monetaryPayment'),
      dataIndex: 'title',
      render: (dataIndex: string) => <PerText fontWeight="bold">{dataIndex}</PerText>,
    },
    {
      key: 'monetaryPaid',
      title:
        t('okr.objective.monetary.toBePaid') +
        ' ' +
        t('appraisalMeeting.oneOffPayment.titleIn') +
        ' ' +
        (objectivePayment && objectivePayment.paymentDate ? formatDateMonthYear(objectivePayment.paymentDate, true) : ''),
      dataIndex: 'amount',
      className: 'textAlign-right',
      render: (dataIndex: JSX.Element) => dataIndex,
    },
  ]

  const generateTableItems = (): PlannedPaymentTableItem[] => {
    const objectiveRows = objectivesToBePaid.map((objective, index) => {
      return {
        id: `${index}${objective.id}${objective.globalId}`,
        title: objective.title,
        amount: getObjectiveAmount(objective.achievedAmount ? objective.achievedAmount : 0, objective.currency!),
      }
    })
    let deductionRow: PlannedPaymentTableItem[] = []
    if (objectivePayment && objectivePayment.deduction && objectivePayment.deductionReason) {
      deductionRow = [
        {
          id: `${objectivePayment.id}_objectivePayment`,
          title: getDeductionTitle(objectivePayment.deductionReason),
          amount: getDeductionAmount(objectivePayment.deduction),
        } as PlannedPaymentTableItem,
      ]
    }
    return [...objectiveRows, ...deductionRow]
  }

  const tableItems: PlannedPaymentTableItem[] = generateTableItems()

  return (
    <>
      {tableItems && tableItems.length > 0 && (
        <Table
          dataSource={tableItems.map((o) => ({ ...o, key: o.id }))}
          columns={columns}
          pagination={false}
          footer={tableItems.length !== 0 ? () => footer() : undefined}
          locale={{
            emptyText: (
              <PerSpacer my="m">
                <div data-e2e-no-oneoffpayment-scheduled>
                  <PerText appearance="info">{t('appraisalMeeting.stepPayment.noPaymentsDescription')}</PerText>
                </div>
                <PerText appearance="info">{t('appraisalMeeting.stepPayment.noPaymentsHint')}</PerText>
              </PerSpacer>
            ),
          }}
        />
      )}
    </>
  )
}
